import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Check, Edit2 } from 'lucide-react';

interface TextInputProps {
  answer: Answer;
  onBlur: (value: { textValue: string }) => void;
  disabled?: boolean;
}

export function TextInput({ answer, onBlur, disabled }: TextInputProps) {
  const [value, setValue] = useState(answer.textValue || '');
  const [isFocused, setIsFocused] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const isTextArea = answer.question.question.length > 50;

  useEffect(() => {
    setValue(answer.textValue || '');
  }, [answer.textValue]);

  const handleBlur = () => {
    setIsFocused(false);
    if (value !== answer.textValue) {
      onBlur({ textValue: value });
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
    }
  };

  const commonStyles = {
    width: '100%',
    padding: spacing[4],
    borderRadius: 16,
    border: `2px solid ${isFocused ? colors.primary[500] : colors.gray[200]}`,
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: isFocused ? colors.white : colors.gray[50],
    color: colors.gray[900],
    resize: 'vertical' as const,
    minHeight: isTextArea ? 120 : 'auto',
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[2],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              display: 'block',
              marginBottom: spacing[1],
            }}
          >
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600], marginBottom: spacing[2] }}>
              {answer.question.description}
            </p>
          )}
        </div>
        
        <AnimatePresence>
          {isSaved && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[1],
                color: colors.success?.[500] || '#10b981',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Check size={16} />
              <span>Guardado</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {isTextArea ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={disabled}
          style={commonStyles}
          placeholder={`Escribe tu respuesta...`}
          maxLength={answer.question.validationRules?.maxLength || undefined}
        />
      ) : (
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            disabled={disabled}
            style={commonStyles}
            placeholder={`Escribe tu respuesta...`}
            maxLength={answer.question.validationRules?.maxLength || undefined}
          />
          {value && !isFocused && (
            <div
              style={{
                position: 'absolute',
                right: spacing[3],
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.gray[400],
              }}
            >
              <Edit2 size={16} />
            </div>
          )}
        </div>
      )}

      {answer.question.validationRules?.maxLength && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: spacing[1],
            fontSize: 12,
            color: value.length > (answer.question.validationRules?.maxLength || 0) * 0.8 
              ? colors.warning?.[500] || '#f59e0b'
              : colors.gray[500],
          }}
        >
          {value.length} / {answer.question.validationRules.maxLength}
        </div>
      )}
    </div>
  );
}